import { useCallback, useEffect } from 'react'
import { Tab } from '@headlessui/react'
import { useRouter } from 'next/router'
import AuthCheck from '@/components/auth/AuthCheck'
import { Type, PageLoader } from '@/components/common'
import { Campaign } from '@/services/api/campaign'
import useAuth from '@/services/hooks/useAuth'
import useNotifications from '@/services/hooks/useNotifications'
import useParty from '@/services/hooks/useParty/useParty'
import useSiteAssets from '@/services/hooks/useSiteAssets'
import { getNextRouterQueryParam } from '@/utils'
import MyInvestmentsNav from './MyInvestmentsNav'
import { UserInvestmentCard, UserPledgeCard } from './UserCampaignCard'
import useMyInvestmentsData from './useMyInvestmentsData'

const TABS = ['investments', 'interest', 'updates']
const getDefaultTabIndex = (initialTab = 'investments') => {
  return TABS.indexOf(initialTab) === -1 ? 0 : TABS.indexOf(initialTab)
}

export const MyInvestments = () => {
  const { party } = useParty()
  const router = useRouter()
  const initialTab = getNextRouterQueryParam('tab', router)
  const toast = getNextRouterQueryParam('toast', router)
  const { addNotification } = useNotifications()
  const { canExpressInterest } = useSiteAssets()

  const { investments, campaigns, pledges, IVAs, payouts, isLoading } =
    useMyInvestmentsData()

  useEffect(() => {
    if (toast) {
      addNotification({
        id: 'my-investments-landing-toast',
        type: 'toast',
        variant: 'error',
        text: decodeURIComponent(toast),
      })
    }
  }, [toast, addNotification])

  if (isLoading) return <PageLoader />

  return (
    <div className="flex min-h-screen flex-col items-center py-6 sm:py-8">
      <div className="mx-auto w-full max-w-[480px]">
        {party && (
          <div className="mb-6 flex flex-col items-center md:mb-8">
            <div className="mb-6 flex h-[80px] w-[80px] items-center justify-center rounded-full bg-[#7EDDE4]">
              <span className="text-[25px] font-semibold text-[#004A59]">
                {`${party.firstName.charAt(0)}${party.lastName.charAt(0)}`}
              </span>
            </div>

            <Type as="h1" variant="title-lg" className="mb-3 font-semibold">
              {party.firstName} {party.lastName}
            </Type>

            <Type as="p" variant="paragraph-md">
              {party.email}
            </Type>

            {party.city && party.country && (
              <Type as="p" variant="paragraph-md">
                {party.city}, {party.country}
              </Type>
            )}
          </div>
        )}

        <Tab.Group defaultIndex={getDefaultTabIndex(initialTab)}>
          <MyInvestmentsNav />
          <Tab.Panels>
            <Tab.Panel className="mt-8 flex flex-col gap-4 px-4 md:px-0">
              {investments.map((investment) => (
                <UserInvestmentCard
                  key={investment.id}
                  campaign={
                    campaigns.find(
                      (c) => c.slug === investment.campaign
                    ) as Campaign
                  }
                  investment={investment}
                  IVA={IVAs.find((IVA) => IVA.investmentId === investment.id)}
                  payout={payouts.find((p) =>
                    p.investments.map((i) => i.id).includes(investment.id)
                  )}
                />
              ))}
              {investments.length === 0 && (
                <Type
                  as="p"
                  variant="paragraph-sm"
                  className="mx-auto max-w-xs text-center text-gray sm:max-w-none"
                >
                  {`You haven't invested in any campaigns yet.`}
                </Type>
              )}
            </Tab.Panel>
            {canExpressInterest && (
              <Tab.Panel className="mt-8 flex flex-col gap-4 px-4 md:px-0">
                {pledges.map((pledge) => (
                  <UserPledgeCard key={pledge.id} pledge={pledge} />
                ))}
                {pledges.length === 0 && (
                  <Type
                    as="p"
                    variant="paragraph-sm"
                    className="mx-auto max-w-xs text-center text-gray sm:max-w-none"
                  >
                    {`You haven't expressed interest in any campaigns yet.`}
                  </Type>
                )}
              </Tab.Panel>
            )}
          </Tab.Panels>
        </Tab.Group>
      </div>
    </div>
  )
}

const GatedMyInvestmentsPage = () => {
  const { startAuth } = useAuth()
  const onUnauthorized = useCallback(startAuth, [startAuth])

  return (
    <AuthCheck onUnauthorized={onUnauthorized}>
      <MyInvestments />
    </AuthCheck>
  )
}

export default GatedMyInvestmentsPage
